import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WorkspaceStorageUsageResponse } from '@inktap/contracts/workspace-storage-usage';
import { approximateBytes, exactBytes, readWorkspaceStorageUsage, StorageUsageReadError, type StorageUsageScope } from '../lib/workspace-storage-usage';
import { useSession, type Session } from '../session';
import './billing-storage-overview.css';

type Observation = { session: Session | null } & (
  { phase: 'loading' } | { phase: 'success'; data: WorkspaceStorageUsageResponse } |
  { phase: 'error'; message: string } | { phase: 'hidden' }
);

function Amount({ value }: { value: string }) {
  const approximate = approximateBytes(value);
  return <>{approximate ? <><span aria-hidden="true">≈ </span><span className="sr-only">Approximately </span>{approximate}<small>Exact: {exactBytes(value)}</small></> : exactBytes(value)}</>;
}

export default function BillingStorageOverview() {
  const { session, setSession } = useSession();
  const currentSession = useRef(session);
  currentSession.current = session;
  const generation = useRef(0);
  const activeRequest = useRef<AbortController | null>(null);
  const pinnedScope = useRef<{ session: Session; scope: StorageUsageScope } | null>(null);
  const [state, setState] = useState<Observation>({ session, phase: 'loading' });

  const refresh = useCallback(() => {
    const requestGeneration = ++generation.current;
    activeRequest.current?.abort();
    activeRequest.current = null;
    if (!session) return;
    const observedSession = session;
    if (pinnedScope.current?.session !== observedSession) pinnedScope.current = null;
    const controller = new AbortController();
    activeRequest.current = controller;
    setState({ session: observedSession, phase: 'loading' });
    const isCurrent = () => !controller.signal.aborted && generation.current === requestGeneration &&
      currentSession.current === observedSession;
    void readWorkspaceStorageUsage({ signal: controller.signal, expectedScope: pinnedScope.current?.scope })
      .then(data => {
        if (!isCurrent()) return;
        pinnedScope.current = { session: observedSession, scope: data.scope };
        setState({ session: observedSession, phase: 'success', data });
      }).catch((error: unknown) => {
        if (!isCurrent()) return;
        const failure = error instanceof StorageUsageReadError ? error : new StorageUsageReadError('unavailable');
        setState({ session: observedSession, phase: 'error', message: failure.message });
        if (failure.reason === 'authentication') setSession(current => current === observedSession ? null : current);
      });
  }, [session, setSession]);

  useEffect(() => {
    refresh();
    const retire = () => {
      ++generation.current;
      activeRequest.current?.abort();
      activeRequest.current = null;
      setState({ session, phase: 'hidden' });
    };
    const visibility = () => { if (document.hidden) retire(); else refresh(); };
    const focus = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('blur', retire);
    window.addEventListener('focus', focus);
    return () => {
      ++generation.current;
      activeRequest.current?.abort();
      activeRequest.current = null;
      pinnedScope.current = null;
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('blur', retire);
      window.removeEventListener('focus', focus);
    };
  }, [refresh, session]);

  const visible = state.session === session ? state : null;
  const data = visible?.phase === 'success' ? visible.data : null;
  const included = data?.capacity.includedBytes == null ? null : BigInt(data.capacity.includedBytes);
  const stored = data ? BigInt(data.currentBillableBytes) : null;
  const percent = included !== null && included > 0n && stored !== null
    ? Number((stored > included ? included : stored) * 10000n / included) / 100 : null;

  return <section className="dashboard-section billing-storage" aria-labelledby="storage-usage-heading">
    <div className="billing-storage__heading"><div><h2 id="storage-usage-heading">Storage usage</h2>
      <p>Stored uploads compared with your current upload plan. Reservations are shown separately.</p></div>
      <Link to="/storage" className="portal-button portal-button--secondary">View storage usage</Link></div>
    {!session ? <p role="status">Sign in to view storage usage.</p> :
      !visible || visible.phase === 'loading' ? <p role="status">Loading storage usage…</p> :
      visible.phase === 'hidden' ? <p role="status">Storage usage will refresh when you return.</p> :
      visible.phase === 'error' ? <div className="billing-storage__error"><p role="status">{visible.message}</p>
        <button type="button" className="button-secondary" onClick={refresh}>Retry storage usage</button></div> : data ? <>
        <div className="billing-storage__values">
          <div><strong><Amount value={data.currentBillableBytes} /></strong><span>Stored uploads</span></div>
          <div><strong>{data.capacity.includedBytes === null ? 'Not specified' : <Amount value={data.capacity.includedBytes} />}</strong><span>Included capacity</span></div>
        </div>
        {percent !== null && data.capacity.includedBytes !== null ? <progress max={100} value={percent}
          aria-label="Stored uploads compared with plan capacity"
          aria-valuetext={exactBytes(data.currentBillableBytes) + ' stored of ' + exactBytes(data.capacity.includedBytes) + ' included'} /> : null}
        {included === 0n ? <p>Current plan capacity is zero bytes.</p> : null}
        {included !== null && stored !== null && stored > included ? <p>Stored uploads exceed the current plan capacity.</p> : null}
        <p className="billing-storage__note">{data.capacity.workspaceKind === 'ORGANIZATION'
          ? 'Usage includes workspace uploads; capacity reflects your plan as acting owner and is not pooled organization capacity.'
          : 'Capacity reflects your current upload plan for this personal workspace.'}</p>
        <p className="billing-storage__note">Approximate amounts use binary units. Reservations and other storage are not included in this meter.</p>
      </> : null}
  </section>;
}
